import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, X, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TYPES } from "@/pages/app/Incidents";
import { ProjectSelect } from "@/components/app/ProjectSelect";
import { useRowKeys } from "@/lib/useRowKeys";

export const compress = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = img.width * scale;
    c.height = img.height * scale;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    resolve(c.toDataURL("image/jpeg", 0.8));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export const PhotoPicker = ({ photos, setPhotos, max = 6 }) => {
  const [keys, drop] = useRowKeys(photos.length);
  const add = async (e) => {
    const files = [...e.target.files].slice(0, max - photos.length);
    try {
      const out = await Promise.all(files.map(compress));
      setPhotos([...photos, ...out]);
    } catch {
      toast.error("Could not read that image");
    }
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-2">
      {photos.map((p, i) => <div key={keys[i]} className="relative h-20 w-20 overflow-hidden rounded-xl border"><img src={p} alt="" className="h-full w-full object-cover" /><button type="button" onClick={() => { drop(i); setPhotos(photos.filter((_, k) => k !== i)); }} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button></div>)}
      {photos.length < max && <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border border-dashed text-muted-foreground hover:bg-accent" data-testid="add-photo"><Camera className="h-5 w-5" /><input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={add} data-testid="photo-input" /></label>}
    </div>
  );
};

const local = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export default function ReportIncident() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ type: "near_miss", title: "", description: "", occurred_at: local(), location: "", severity: "minor", immediate_actions: "", project_id: params.get("project") || "" });
  const [people, setPeople] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/incidents", { ...f, project_id: f.project_id || null, occurred_at: new Date(f.occurred_at).toISOString(), people: people.filter((p) => p.name.trim()), photos });
      toast.success(`${data.ref} reported — thank you`);
      nav(`/app/incidents/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mx-auto max-w-2xl space-y-5" data-testid="report-incident-form">
      <div><p className="eyebrow">Report</p><h1 className="mt-2 text-3xl font-bold">Report an incident</h1>
        <p className="text-sm text-muted-foreground">Record the facts as you know them. If someone is hurt or in danger, get help first.</p></div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{Object.entries(TYPES).map(([k, l]) => (
        <button type="button" key={k} onClick={() => setF({ ...f, type: k })} data-testid={`type-${k}`} className={`rounded-xl border px-3 py-3 text-sm ${f.type === k ? "border-health bg-health/10 font-medium" : "hover:bg-accent"}`}>{l}</button>))}</div>
      <div className="space-y-1"><Label>Short title</Label><Input required minLength={3} value={f.title} onChange={set("title")} placeholder="Pallet fell from racking in aisle 3" data-testid="incident-title" /></div>
      <div className="space-y-1"><Label>What happened?</Label><Textarea required minLength={10} rows={5} value={f.description} onChange={set("description")} placeholder="Describe what you saw, in order. Stick to facts." data-testid="incident-description" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label>When</Label><Input type="datetime-local" required value={f.occurred_at} onChange={set("occurred_at")} data-testid="incident-when" /></div>
        <div className="space-y-1"><Label>Where</Label><Input value={f.location} onChange={set("location")} placeholder="Site, building, area" data-testid="incident-location" /></div>
      </div>
      <div className="space-y-1"><Label>Project (optional)</Label><ProjectSelect value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })} testid="incident-project" /></div>
      <div className="space-y-1"><Label>How serious does it seem?</Label>
        <div className="flex flex-wrap gap-2">{["minor", "moderate", "major", "critical"].map((s) => <button type="button" key={s} onClick={() => setF({ ...f, severity: s })} data-testid={`sev-${s}`} className={`rounded-full border px-4 py-1.5 text-sm capitalize ${f.severity === s ? "border-navy bg-navy text-white dark:border-health dark:bg-health dark:text-navy" : ""}`}>{s}</button>)}</div></div>
      <div className="space-y-2"><Label>People involved (optional)</Label>
        {people.map((p, i) => <div key={i} className="grid gap-2 rounded-xl border p-3 sm:grid-cols-3">{["name", "role", "injury"].map((k) => <Input key={k} placeholder={k === "injury" ? "Injury (if any)" : k[0].toUpperCase() + k.slice(1)} value={p[k]} onChange={(e) => setPeople(people.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />)}</div>)}
        <Button type="button" variant="outline" size="sm" onClick={() => setPeople([...people, { name: "", role: "", injury: "" }])} data-testid="add-person"><Plus className="mr-1 h-3 w-3" />Add person</Button></div>
      <div className="space-y-1"><Label>Immediate actions taken</Label><Textarea rows={3} value={f.immediate_actions} onChange={set("immediate_actions")} placeholder="Area cordoned off, first aid given, supervisor informed…" data-testid="incident-immediate" /></div>
      <div className="space-y-2"><Label>Photos (up to 6)</Label><PhotoPicker photos={photos} setPhotos={setPhotos} /></div>
      <Button type="submit" size="lg" className="w-full rounded-full" disabled={busy} data-testid="incident-submit">{busy ? "Submitting…" : "Submit report"}</Button>
    </form>
  );
}
